import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { useFetchForms } from '../hooks/useFetchForms';
import { feedbackTypes } from '../constants/general';
import { SectionLoader } from './general/Loaders';
import { Send } from 'lucide-react';
import { hexToRgb } from '../utils';
import { useAddSubmissionMutation } from '../services/api';
import { successToast, errorToast } from '../utils/toasts';
import Select, { components } from 'react-select';
import clsx from 'clsx';

export const FeedbackForm = () => {
  const navigate = useNavigate();

  const { id } = useParams();

  const [formData, setFormData] = useState(null);
  const [feedbackFormData, setFeedbackFormData] = useState(null);
  const [gradientStyle, setGradientStyle] = useState(null);
  const [displayErrorMessage, setDisplayErrorMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { forms, isLoading, isError } = useFetchForms({ id: id });

  const [addSubmissionTrigger] = useAddSubmissionMutation();

  const isFormValid = values => {
    if (!values.feedbackType) return false;
    const missingRequiredFields = feedbackFormData?.fields.some(
      field => field.required && !values[field.id],
    );
    return !missingRequiredFields;
  };

  const getBrowserInfo = () => {
    const ua = navigator.userAgent;

    if (ua.includes('Firefox/')) return 'Firefox';
    if (ua.includes('Edg/')) return 'Edge';
    if (ua.includes('Chrome/')) return 'Chrome';
    if (ua.includes('Safari/') && !ua.includes('Chrome/')) return 'Safari';
    if (ua.includes('Opera/') || ua.includes('OPR/')) return 'Opera';

    return 'Unknown';
  };

  const transformDataWithLabels = (fields, values) => {
    const result = {};

    // only keep the relevant keys that we want to display the label and value for
    const { feedbackType, userEmail, asanaTaskName, ...rest } = values;

    // copy over any properties that aren't in the fields array
    for (const key in rest) {
      const field = fields.find(f => f.id === key);
      if (!field) {
        result[key] = values[key];
      }
    }

    // add the label and inputted value to each key
    fields.forEach(field => {
      if (rest.hasOwnProperty(field.id)) {
        result[field.id] = {
          label: field.label,
          value: rest[field.id],
        };
      }
    });

    return result;
  };

  const sendFeedbackForm = values => {
    if (!isFormValid(values)) {
      setDisplayErrorMessage(true);
      return;
    } else {
      setDisplayErrorMessage(false);
    }

    setIsSubmitting(true);

    const submissionData = {
      sourceSiteId: formData?.id,
      asanaProjectId: formData?.asana_project_id,
      asanaTaskName: values.asanaTaskName,
      feedbackType: values.feedbackType?.value || null,
      assigneeId: formData?.asana_assignee_id || null,
      userEmail: values.userEmail,
      formFields: transformDataWithLabels(feedbackFormData.fields, values),
      platform: navigator?.platform,
      browser: getBrowserInfo(),
      isMobile: navigator?.userAgentData?.mobile,
    };

    addSubmissionTrigger(submissionData)
      .unwrap()
      .then(_ => {
        successToast('Feedback form submitted successfully!');
        navigate('submitted');
      })
      .catch(error => {
        console.error(error);
        errorToast('An error occurred while submitting the form. Please try again.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const formik = useFormik({
    initialValues: {},
    onSubmit: sendFeedbackForm,
  });

  // Initialize formik initial values with the feedback fields from the form
  useEffect(() => {
    if (forms[0]?.feedback_forms?.length) {
      const initialValues = forms[0].feedback_forms[0].fields.reduce((acc, field) => {
        acc[field.id] = '';
        return acc;
      }, {});
      formik.setValues({ feedbackType: '', ...initialValues });
    }
  }, [forms]);

  useEffect(() => {
    // check that the source
    if (forms && forms?.length && forms[0]?.feedback_forms?.length) {
      setFormData(forms[0]);
      setFeedbackFormData(forms[0].feedback_forms[0]);

      const rgb = hexToRgb(forms[0].feedback_forms[0]?.color);
      const gradient = {
        background: `linear-gradient(to bottom right, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1), white)`,
      };
      setGradientStyle(gradient);
    }
  }, [forms]);

  const renderField = field => {
    const baseClasses =
      'w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all';
    const focusRingStyle = { '--tw-ring-color': field.color };

    switch (field.type) {
      case 'text':
      case 'email':
        return (
          <input
            id={field.id}
            type={field.type}
            value={formik.values[field.id]}
            onChange={formik.handleChange}
            className={baseClasses}
            style={{ fontFamily: field.font, ...focusRingStyle }}
          />
        );

      case 'number':
        return (
          <input
            id={field.id}
            type="number"
            value={formik.values[field.id]}
            onChange={formik.handleChange}
            min={field.min}
            max={field.max}
            className={baseClasses}
            style={{ fontFamily: field.font, ...focusRingStyle }}
          />
        );

      case 'dropdown':
        return (
          <select
            id={field.id}
            value={formik.values[field.id]}
            onChange={formik.handleChange}
            className={baseClasses}
            style={{ fontFamily: field.font, ...focusRingStyle }}
          >
            <option value="">--Select--</option>
            {field.options?.map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            id={field.id}
            value={formik.values[field.id]}
            onChange={formik.handleChange}
            rows={6}
            className={`${baseClasses} resize-none`}
            style={{ fontFamily: field.font, ...focusRingStyle }}
          />
        );

      case 'date':
        return (
          <input
            id={field.id}
            type="date"
            value={formik.values[field.id]}
            onChange={formik.handleChange}
            className={baseClasses}
            style={{ fontFamily: field.font, ...focusRingStyle }}
          />
        );

      default:
        return null;
    }
  };

  const Option = props => {
    const Icon = props.data.icon;
    return (
      <components.Option {...props}>
        <div className="flex items-center gap-3 py-1">
          <Icon className={clsx('w-5 h-5', props.data.color)} />
          <span>{props.data.label}</span>
        </div>
      </components.Option>
    );
  };

  const SingleValue = props => {
    const Icon = props.data.icon;
    return (
      <components.SingleValue {...props}>
        <div className="flex items-center gap-3">
          <Icon className={clsx('w-5 h-5', props.data.color)} />
          <span>{props.data.label}</span>
        </div>
      </components.SingleValue>
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-4 my-12">
        <SectionLoader dotColor="slate" />
        <p>Loading...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="bg-gradient-to-br from-red-50 to-white pt-12 flex-1 flex items-center justify-center">
          <div className="container mx-auto px-6">
            <div className="max-w-2xl mx-auto text-center">
              <div className="mb-8">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-red-100 rounded-full">
                  <svg
                    className="w-12 h-12 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              </div>

              <h2 className="text-4xl md:text-5xl font-light text-black mb-4">Form Not Found</h2>
              <p className="text-xl text-gray-600 font-light mb-8">
                We couldn't load the feedback form you're looking for. It may have been removed or
                the link might be incorrect.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/')}
                  className="bg-navy text-white font-medium py-3 px-6 rounded-lg hover:bg-opacity-90 transition-all"
                >
                  Go to Homepage
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!formData || !feedbackFormData) {
    return (
      <div className="flex flex-col items-center gap-4 my-12">
        <SectionLoader dotColor="slate" />
        <p>Loading form...</p>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ fontFamily: feedbackFormData?.font || 'Open Sans' }}
    >
      <div className="pt-12" style={gradientStyle}>
        {/* Header */}
        <div className="flex flex-col items-center mb-12 px-6">
          <h2 className="text-center text-4xl md:text-5xl font-light text-black mb-6">
            {feedbackFormData?.title}
          </h2>
          {feedbackFormData?.description && (
            <p className="text-center text-gray-600 text-lg mb-4">{feedbackFormData.description}</p>
          )}
          {formData?.site_url && feedbackFormData?.display_site_url && (
            <a
              href={formData.site_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-baseline text-navy hover:underline mt-2"
            >
              Visit {formData?.site_name || ''} site →
            </a>
          )}
        </div>

        {/* Form Fields */}
        <form onSubmit={formik.handleSubmit} className="flex flex-col items-center mb-20">
          <div className="w-full max-w-lg px-4 mb-10">
            <div className="space-y-8">
              <div>
                <label className="block text-base xl:text-lg font-medium text-gray-700 mb-2 ml-2">
                  Feedback type
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <Select
                  options={feedbackTypes}
                  value={formik.values.feedbackType}
                  onChange={value => {
                    formik.setFieldValue('feedbackType', value);
                  }}
                  components={{ Option, SingleValue }}
                  placeholder="Select feedback type..."
                  styles={{
                    input: base => ({
                      ...base,
                      marginLeft: 10,
                      padding: 0,
                      '& input': {
                        padding: '0 !important',
                        margin: '0 !important',
                        paddingLeft: '0 !important',
                      },
                      'input:focus': {
                        boxShadow: 'none',
                      },
                    }),
                    valueContainer: base => ({
                      ...base,
                      padding: '2px 8px',
                      paddingLeft: '8px',
                    }),
                    placeholder: base => ({
                      ...base,
                      marginLeft: 10,
                    }),
                    singleValue: base => ({
                      ...base,
                      marginLeft: 10,
                    }),
                  }}
                />
              </div>
              {feedbackFormData?.fields.map((field, index) => (
                <div key={index}>
                  <label className="block text-base xl:text-lg font-medium text-gray-700 mb-2 ml-2">
                    {field.label.charAt(0).toUpperCase() + field.label.slice(1)}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {renderField(field)}
                </div>
              ))}

              {displayErrorMessage ? (
                <p className="italic text-red-500">
                  Please select a feedback type and complete all required fields
                </p>
              ) : null}

              {/* Submit button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex gap-2 items-center justify-self-center bg-navy text-white font-medium py-4 px-6 rounded-lg"
              >
                {isSubmitting ? (
                  <>Submitting...</>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Feedback
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
